/**
 * Card FX events (task 186, extracted for testability — task 196): the
 * one-shot 'cardEvents' batch that each authoritative GameStateUpdate
 * carries from the server (RoundCoordinator.applyOverdraw → the match
 * summary → the wire). Two event types:
 *
 *  - 'overdraw': a draw that did not fit in the FULL hand — the card was
 *    LOST. Play a RED CARD-LIKE EXPLOSION over the player's hand strip
 *    (any overdraw — Sacrificial Rune or not).
 *  - 'cardDestroyed': the Sacrificial Rune evaporated a random hand card
 *    — BURNING PARTICLES at the card's last on-screen position.
 *
 * Consumption is REFERENCE-GUARDED: each batch is a fresh array per
 * update, so `batch !== lastRef` means "not consumed yet" — the FX never
 * replays on timer ticks / re-renders. The function is PURE with respect
 * to the game state (it only plays FX + sound); the caller stores the
 * returned reference as the new "last consumed" marker.
 */

import type { CardFxEvent } from '../../shared/messages/MessageContracts';
import type { SoundName } from './SoundService';
import type { FxCanvas } from './FxCanvas';

/** The FX sinks (injected so tests can stub/spy). */
export interface CardFxSinks {
  fx: FxCanvas;
  play: (name: SoundName) => void;
}

/** The per-player render targets for the effects. */
export interface CardFxTargets {
  /** The local player's hand strip (the #hand container). */
  handEl: Element;
  /** The local player's id. */
  myId: string | null;
  /** Hand card zoom (the overdraw burst magnifies with the hand). */
  handScale: number;
  /** COOP: the teammate's id + hand element + whether the overlay is open. */
  teammateId: string | null;
  teammateHandEl: Element | null;
  teammateOverlayOpen: boolean;
}

/**
 * Consumes one 'cardEvents' batch: plays the FX for every event it has
 * not consumed before (the reference guard) and returns the new "last
 * consumed" reference (the batch itself, or `lastRef` when the batch is
 * empty / already consumed — the caller stores it).
 *
 * The rects are captured NOW by the caller's DOM order: the destroyed
 * card's element is removed by the hand rebuild that runs AFTER this
 * call, so the position is measured while the card is still on screen.
 */
export function consumeCardFxEvents(
  batch: CardFxEvent[] | null | undefined,
  lastRef: ReadonlyArray<CardFxEvent> | null,
  sinks: CardFxSinks,
  targets: CardFxTargets,
  findCardEl: (instanceId: string) => HTMLElement | null,
): ReadonlyArray<CardFxEvent> | null {
  if (!batch || batch === lastRef || batch.length === 0) return lastRef;

  for (const ev of batch) {
    if (ev.type === 'overdraw') {
      // Red card-like explosion over the player whose draw was lost (the
      // local player's hand strip; the COOP teammate's hand while their
      // overlay is open — a closed overlay has nowhere to play it).
      // TASK 201: when the event carries the LOST card's face (ev.card),
      // the main.ts GHOST flow owns the effect — the real card is drawn
      // into the hand and the burst plays at the card's own position
      // (sequentially for multiple overdraws). Nothing is played here.
      if (ev.playerId === targets.myId && ev.card) {
        // ghost flow (main.ts) — see above.
      } else if (ev.playerId === targets.myId) {
        sinks.fx.burstEl(targets.handEl, 'overdraw', { scale: targets.handScale });
        sinks.play('explode');
      } else if (targets.teammateOverlayOpen && ev.playerId === targets.teammateId) {
        sinks.fx.burstEl(targets.teammateHandEl, 'overdraw');
        sinks.play('explode');
      }
    } else if (ev.type === 'cardDestroyed' && ev.instanceId) {
      // Burning-particle evaporation at the card's last on-screen
      // position (main hand, or the teammate overlay's hand).
      const el = findCardEl(ev.instanceId);
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0) {
          sinks.fx.burstAt(r.left + r.width / 2, r.top + r.height / 2, 'evaporate');
          sinks.play('impact');
        }
      }
    }
  }

  return batch;
}
